/* eslint-disable */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import BG from './BG';
// import Lens from './Lens';
import Footer from './Footer';
import { setTheme } from './themes';
import getElOffsetFromCenter from './getElOffsetFromCenter';

/**
 * Animation that appears on the homepage
 *
 * @class    HomepageAnim
 * @param    {object}  options  Options for the object
 * @return   {object}  The object
 */
const HomepageAnim = function(options) {
  //
  //   Public Vars
  //
  //////////////////////////////////////////////////////////////////////

  let self = Object.assign(
    {},
    {
      animationContainerSel: '[data-animation-container]',
    },
    options
  );

  //
  //   Private Vars
  //
  //////////////////////////////////////////////////////////////////////

  let bg;
  let lens;
  let footer;
  const isHome = window.APP.isHome;

  //
  //   Private Methods
  //
  //////////////////////////////////////////////////////////////////////

  const _init = () => {
    import(/* webpackChunkName: "pixi" */ 'pixi.js').then(PIXI => {
      _addElements();
      _addStyles();

      _setup(PIXI);
      _update();
      _addGlobalMethods();
      _addEventListeners();
    });
  };

  const _addElements = () => {
    const markup = `
      <div class="lens-wrapper" data-lens-wrapper></div>
      <canvas class="bg-canvas" data-bg-canvas></canvas>
      <span class="lens-ring" data-lens-ring>
        <span class="lens-ring__ring">
          <svg id="Layer_1" xmlns="http://www.w3.org/2000/svg" x="0" y="0" viewBox="0 0 350 350" xml:space="preserve">
            <path d="M175 6c93.3 0 169 75.7 169 169s-75.7 169-169 169S6 268.3 6 175 81.7 6 175 6" fill="none" stroke="currentColor" stroke-width="12" stroke-miterlimit="10"/>
          </svg>
        </span>
      </span>
    `;

    const container = document.querySelector(self.animationContainerSel);
    if (!container)
      return console.error(
        'Could not find an element matching `<div class="animation-container" data-animation-container></div>`'
      );
    container.innerHTML = markup;
  };

  const _addStyles = () => {

    const smallLensSize = '84px';
    const largeLensSize = '155px';
    let lensSize = smallLensSize;

    if(isHome){
      lensSize = largeLensSize;
    }
    const css = `
      :root {
        --homepage-anim-theme-color-bg-fill: '#fff',
        --homepage-anim-theme-color-bg-stroke: '#fff',
        --homepage-anim-theme-color-ring-stroke: '#fff',
      }

      .dg.ac {
        z-index: 999;
      }

      .animation-container {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        overflow: hidden;
      }

      /* At larger sizes, make the background animation fixed */
      // @media screen and (min-width: 1024px) {
      //   .animation-container {
      //     position: fixed;
      //   }
      // }

      .lens-ring {
        display: block;
        left: 0;
        top: 0;
        position: absolute;
        width: ${smallLensSize};
        height: ${smallLensSize};
        top: 0;
        left: 0;
        transition: opacity 3s;
      }

      .lens-ring__ring {

      }

      .lens-ring__ring svg {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
      }

      .lens-ring__ring path {
        stroke-dashoffset: 1061.94482421875;
        transition: 2s cubic-bezier(0.4, 0, 0.04, 1);
        transition-property: stroke-dashoffset, opacity;
        stroke-dasharray: 1061.94482421875;
        opacity: 0;
        color: var(--homepage-anim-theme-color-ring-stroke);
      }

      .lens-ring.is-visible .lens-ring__ring path {
        stroke-dashoffset: 0;
        opacity: 1;
      }

      .lens-wrapper {
        position: absolute;
        overflow: hidden;
        border-radius: 50%;
        width: ${smallLensSize};
        height: ${smallLensSize};
        top: 0;
        left: 0;
        opacity: 0;
        transition: opacity 3s;
      }

      .lens-wrapper.is-visible {
        opacity: 1;
      }

      @media screen and (min-width: 768px) {
        .lens-ring {
          width: ${lensSize};
          height: ${lensSize};
          top: 0;
          left: 0;
        }

        .lens-wrapper {
          width: ${lensSize};
          height: ${lensSize};
          top: 0;
          left: 0;
        }
      }

      .bg-canvas {
        position: fixed;
        left: 0;
        top: 0;
        bottom: 0;
        opacity: 0;
        transition: opacity 3s;
        width: 100%;
      }
    `;

    let head = document.head || document.getElementsByTagName('head')[0];
    let style = document.createElement('style');
    head.appendChild(style);
    style.appendChild(document.createTextNode(css));
  };

  const _setup = PIXI => {
    // BG
    bg = new BG();

    // Lens
    // lens = new Lens(PIXI);

    // Footer
    footer = new Footer(PIXI);
  };

  const _addEventListeners = () => {
    gsap.registerPlugin(ScrollTrigger);

    // Start and stop the footer when it enters the viewport
    ScrollTrigger.create({
      trigger: '[data-footer]',
      start: 'top 103%',
      onToggle: ({ isActive }) => {
        if (isActive) {
          footer.start();
        } else {
          footer.stop();
        }
      },
    });

    if (window.entry.backgroundAnimation || isHome) {
      // Reveal the background animation
      self.reveal({
        showLens: true,
        theme: window.entry.backgroundColor,
      });

      // Find the first element with a solid background
      const pauseTrigger = document.querySelector('.has-bg-color');

      // When that element hits the top of the viewport, halt the animation
      ScrollTrigger.create({
        trigger: pauseTrigger,
        start: 'top -50px',
        end: 'top -100000px',
        onToggle: ({ isActive }) => {
          if (isActive) {
            self.stopBg();
          } else {
            self.startBg({ startLens: true });
          }
        },
      });

      // Handle lens movement on homepage
      // if (isHome) {
        const firstSectionEl = document.querySelector(
          '[data-page-content] section'
        );
        let heroOffset;
        let heroContainer = document.querySelector('.header-lens-wrapper');

        // const onResize = () => {
        //   if (window.APP.isMqMedium()) {
        //     lens.setLensOffsetY(heroContainer.offsetTop);
        //     lens.setLensOffsetX(heroContainer.offsetLeft);
        //   } else {
        //     lens.setLensOffsetY(heroContainer.offsetTop);
        //     lens.setLensOffsetX(heroContainer.offsetLeft);
        //   }
        // };

        // Listen for resizes
        // window.addEventListener('resize', onResize);

        // Trigger initial resize
        // onResize();

        // Lens viewport position - desktop
        ScrollTrigger.create({
          trigger: 'body',
          start: 'top top',
          endTrigger: firstSectionEl,
          end: 'bottom top',
          onUpdate: scroller => {
            // Doesn't apply to mobile
            if (window.APP.isMqMedium()) return;

            // const firstSectionCenter = window.innerHeight / 2 - heroOffset;

            // lens.scrollOffsetY =
            //   scroller.scroll() > firstSectionCenter
            //     ? -heroOffset - Math.abs(scroller.scroll() - firstSectionCenter)
            //     : -heroOffset;
          },
        });
      // }
    }
  };

  const _update = () => {
    if (bg) bg.update();

    window.requestAnimationFrame(_update);
  };

  const _addGlobalMethods = () => {
    window.revealAnimation = self.reveal;
  };

  //
  //   Public Methods
  //
  //////////////////////////////////////////////////////////////////////

  self.reveal = (options = {}) => {
    const { showLens = true, theme = 'dark' } = options;

    // Set the theme
    setTheme(theme);

    // Start lens (if applicable)
    if (showLens) {
      // lens.start();
    }

    // Start the background animation
    bg.start();

    const tl = gsap.timeline();

    // Start drawing in the ring
    // if (lens) {
    //   tl.add(window.revealLensRing, '+=0.3');
    // }

    // Reveal BG
    tl.add(window.revealBg, '+=0.2');

    // Reveal lens
    // if (lens) {
    //   tl.add(window.revealLens, '+=0.1');
    // }

    // Increase lens and bg reach
    tl.add(() => {
      // window.setLensVerticalOffset(0);
      // window.restoreLensReach();
      window.restoreBgReach();
    }, '+=0.7');

    return tl.then();
  };

  self.startBg = (options = {}) => {
    // const { startLens = true } = options;
    bg.start();
    // if (startLens) lens.start();
  };

  self.stopBg = () => {
    bg.stop();
    // lens.stop();
  };

  //
  //   Initialize
  //
  //////////////////////////////////////////////////////////////////////

  _init();

  // Return the Object
  return self;
};

export default HomepageAnim;
